import { FadeProps } from "@remotion/transitions/fade";
import {
  getDefaultPreviewClipDurationsInFrames,
  ImageTransitionBuilder,
  ImageTransitionWrapperProps,
} from "./ImageTransitionBuilder.ts";
import { TransitionKind, TransitionSpeed } from "@repo/api/transition";
import { interpolate } from "remotion";
import { easeInCubic, easeOutSine } from "../../utils/easing.ts";
import { msToFrames } from "../../utils/timing.ts";

const transitionKind = TransitionKind.enum.FADE_BLACK;

export const ImageFadeBlackTransitionBuilder: ImageTransitionBuilder<FadeProps> =
  {
    formConfig: {
      kind: transitionKind,
      buildDefaultValue: () => ({ kind: transitionKind, speed: "NORMAL" }),
      speed: ["SLOW", "NORMAL", "FAST"],
    },
    wrappers: {
      nextImageWrapper: {
        overlayComponent: NextImageOverlay,
      },

      prevImageWrapper: {
        overlayComponent: PrevImageOverlay,
      },
    },

    getPreviewClipDurationsInFrames: getDefaultPreviewClipDurationsInFrames,
  };

const NEXT_IMAGE_DURATIONS = {
  SLOW: 2000,
  NORMAL: 1000,
  FAST: 500,
};

const PREV_IMAGE_DURATIONS = {
  SLOW: 1000,
  NORMAL: 500,
  FAST: 250,
};

function getDurationInFrames(
  speed: TransitionSpeed | undefined,
  speedMapping: Record<TransitionSpeed, number>,
  fps: number,
) {
  if (!speed) {
    return null;
  }

  return msToFrames(speedMapping[speed], fps);
}

function NextImageOverlay({
  fps,
  currentFrame,
  imageTransition,
}: ImageTransitionWrapperProps) {
  const endFrame = getDurationInFrames(
    imageTransition.speed,
    NEXT_IMAGE_DURATIONS,
    fps,
  );

  if (!endFrame) {
    return null;
  }

  const startOpacity = interpolate(currentFrame, [0, endFrame], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeOutSine,
  });

  if (currentFrame > endFrame) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 bg-black"
      style={{ opacity: startOpacity }}
    ></div>
  );
}

function PrevImageOverlay({
  imageDurationInFrames,
  fps,
  currentFrame,
  imageTransition,
}: ImageTransitionWrapperProps) {
  const transitionDurationFrames = getDurationInFrames(
    imageTransition.speed,
    PREV_IMAGE_DURATIONS,
    fps,
  );

  if (!transitionDurationFrames) {
    return null;
  }

  const endFrame = Math.max(1, imageDurationInFrames - 1);
  const startFrame = Math.max(0, endFrame - transitionDurationFrames);

  const endOpacity = interpolate(currentFrame, [startFrame, endFrame], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInCubic,
  });

  if (currentFrame < startFrame) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 bg-black"
      style={{ opacity: endOpacity }}
    ></div>
  );
}
